import * as React from 'react'

import Box from 'components_new/atoms/Box'

import AddButton from '../AddButton'
import { FieldOption } from '../FieldSelect'
import { BOARD } from '../accents'

import { FilterCondition } from 'types/transformation/types/nodes'
import Operator from 'types/transformation/enums/filter/Operator'
import Condition from 'types/transformation/enums/filter/Condition'
import FilterConditionType from 'types/transformation/enums/filter/Type'

import { FilterConditionRow } from './FilterCondition'

interface FilterBuilderProps {
  value: FilterCondition[]
  onChange: (next: FilterCondition[]) => void
  columns: FieldOption[]
}

/** Segmented AND/OR joiner shown between two consecutive conditions. */
const JoinerToggle = (props: {
  value: Operator
  onChange: (op: Operator) => void
}) => {
  const { value, onChange } = props

  return (
    <Box
      sx={{
        backgroundColor: BOARD.chipBackground,
        borderRadius: '8px',
        display: 'flex',
        gap: '2px',
        my: 1.25,
        p: '2px'
      }}
    >
      {[
        { op: Operator.AND, label: 'AND' },
        { op: Operator.OR, label: 'OR' }
      ].map(({ op, label }) => {
        const active = value === op

        return (
          <Box
            key={label}
            role="button"
            onClick={() => onChange(op)}
            sx={{
              backgroundColor: active ? 'common.white' : 'transparent',
              borderRadius: '6px',
              boxShadow: active ? '0 1px 2px rgba(16, 24, 40, 0.08)' : 'none',
              color: active ? BOARD.textPrimary : BOARD.textMuted,
              cursor: 'pointer',
              flex: 1,
              fontFamily: BOARD.fontBody,
              fontSize: '11.5px',
              fontWeight: 600,
              py: '4px',
              textAlign: 'center'
            }}
          >
            {label}
          </Box>
        )
      })}
    </Box>
  )
}

export default function FilterBuilder({
  value,
  onChange,
  columns
}: FilterBuilderProps) {
  // The engine evaluates conditions in `index` order (operator[i] joins
  // condition[i] -> condition[i+1]), so render and mutate in that same order.
  // The incoming array order is not guaranteed to match `index`.
  const rows = React.useMemo(
    () => [...value].sort((a, b) => a.index - b.index),
    [value]
  )

  const handleRowChange = (idx: number, nextRow: FilterCondition) => {
    onChange(rows.map((row, i) => (i === idx ? nextRow : row)))
  }

  const handleRemove = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx))
  }

  const handleAdd = () => {
    const last = rows[rows.length - 1]

    onChange([
      ...rows,
      {
        left_column: null,
        type: FilterConditionType.INPUT_VALUE,
        condition: Condition.EQ,
        operator: last?.operator ?? Operator.AND,
        right_value: null,
        right_column: null,
        index: rows.length
      }
    ])
  }

  const handleOperatorChange = (idx: number, op: Operator) => {
    onChange(
      rows.map((row, i) => (i === idx ? { ...row, operator: op } : row))
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((row, i) => (
        <Box key={`cond-${i}`}>
          <FilterConditionRow
            index={i}
            condition={row}
            columns={columns}
            onChange={handleRowChange}
            onRemove={handleRemove}
          />
          {i < rows.length - 1 ? (
            <JoinerToggle
              value={row.operator}
              onChange={(op) => handleOperatorChange(i, op)}
            />
          ) : null}
        </Box>
      ))}

      <Box sx={{ mt: 1 }}>
        <AddButton onClick={handleAdd} />
      </Box>
    </Box>
  )
}
